/**
 * /fleet — rejilla de vehículos con búsqueda y filtros.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { LayoutGrid, List, Plus, Search, Truck } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { StatusPill } from '@/components/ui/status-pill'
import { listVehicles, type Vehicle } from '@/lib/api'
import { cn } from '@/lib/utils'

const TYPES = ['', 'Car', 'Van', 'Truck', 'Box Truck', 'Forklift', 'Trailer', 'Other'] as const
const STATUSES = ['', 'Active', 'In Repair', 'Out of Service', 'Sold', 'Retired'] as const

function VehicleCard({ v }: { v: Vehicle }) {
  const title = [v.year, v.make, v.model].filter(Boolean).join(' ') || v.unitNumber
  return (
    <Link
      to="/fleet/$vehicleId"
      params={{ vehicleId: v.id }}
      className="block rounded-2xl border border-border bg-card shadow-card transition-colors hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <div className="aspect-[16/10] overflow-hidden rounded-t-2xl bg-secondary">
        {v.primaryPhotoUrl ? (
          <img
            src={v.primaryPhotoUrl}
            alt=""
            className="size-full object-cover"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <div className="grid size-full place-items-center text-muted-foreground">
            <Truck className="size-10" aria-hidden />
          </div>
        )}
      </div>
      <div className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{v.unitNumber}</p>
            <p className="truncate text-xs text-muted-foreground">{title}</p>
          </div>
          <StatusPill status={v.status} />
        </div>
        <p className="text-xs text-muted-foreground">
          {v.vehicleType}
          {v.licensePlate ? ` · ${v.licensePlate}` : ''}
          {v.currentOdometer
            ? ` · ${v.currentOdometer.toLocaleString()} ${v.odometerUnit}`
            : ''}
        </p>
      </div>
    </Link>
  )
}

/**
 * Fila compacta. En una flota de 19 unidades las tarjetas se ven bien, pero con
 * cien hay que bajar mucho para encontrar una: el listado enseña seis veces más
 * unidades por pantalla y prioriza los datos sobre la foto.
 */
function VehicleRow({ v }: { v: Vehicle }) {
  const title = [v.year, v.make, v.model].filter(Boolean).join(' ') || '—'
  return (
    <Link
      to="/fleet/$vehicleId"
      params={{ vehicleId: v.id }}
      className="flex min-h-[60px] items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
    >
      <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-secondary">
        {v.primaryPhotoUrl ? (
          <img
            src={v.primaryPhotoUrl}
            alt=""
            className="size-full object-cover"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <Truck className="size-5 text-muted-foreground" aria-hidden />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{v.unitNumber}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {title}
          {v.licensePlate ? ` · ${v.licensePlate}` : ''}
        </span>
      </span>
      <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
        {v.currentOdometer ? `${v.currentOdometer.toLocaleString()} ${v.odometerUnit}` : '—'}
      </span>
      <StatusPill status={v.status} />
    </Link>
  )
}

type ViewMode = 'grid' | 'list'
const VIEW_KEY = 'gkdfleet:fleet-view'

export function FleetPage() {
  const [q, setQ] = useState('')
  const [type, setType] = useState('')
  const [status, setStatus] = useState('')
  // La preferencia se recuerda entre visitas: quien trabaja con listado no
  // quiere volver a cambiarlo cada vez que entra.
  const [view, setView] = useState<ViewMode>(() =>
    (localStorage.getItem(VIEW_KEY) as ViewMode | null) === 'list' ? 'list' : 'grid',
  )

  function changeView(next: ViewMode) {
    setView(next)
    localStorage.setItem(VIEW_KEY, next)
  }

  const query = useQuery({
    queryKey: ['vehicles', q, type, status],
    queryFn: () =>
      listVehicles({
        q: q || undefined,
        type: type || undefined,
        status: status || undefined,
      }),
  })

  const vehicles = query.data?.vehicles ?? []
  const countLabel = useMemo(() => {
    if (query.isLoading) return 'Cargando…'
    return `${vehicles.length} unidad${vehicles.length === 1 ? '' : 'es'}`
  }, [query.isLoading, vehicles.length])

  return (
    <>
      <PageHeader
        title="Fleet"
        subtitle={countLabel}
        actions={
          <Link
            to="/fleet/new"
            className="inline-flex h-11 min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:h-10 lg:min-h-10"
          >
            <Plus className="size-4" aria-hidden />
            Add vehicle
          </Link>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Buscar</span>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Unidad, VIN, placa, marca…"
            className="h-11 w-full rounded-xl border border-border bg-card pr-3 pl-10 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <select
          aria-label="Tipo"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="h-11 rounded-xl border border-border bg-card px-3 text-sm"
        >
          <option value="">Todos los tipos</option>
          {TYPES.filter(Boolean).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          aria-label="Estado"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-11 rounded-xl border border-border bg-card px-3 text-sm"
        >
          <option value="">Todos los estados</option>
          {STATUSES.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {/* role="group" con aria-pressed: son dos estados de una misma vista, no
            dos acciones sueltas, y así un lector de pantalla dice cuál está
            activa. */}
        <div
          role="group"
          aria-label="Forma de ver la flota"
          className="flex shrink-0 rounded-xl border border-border bg-card p-1"
        >
          <button
            type="button"
            onClick={() => changeView('grid')}
            aria-pressed={view === 'grid'}
            title="Ver en cuadros"
            className={cn(
              'inline-flex size-9 cursor-pointer items-center justify-center rounded-lg transition-colors',
              view === 'grid'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <LayoutGrid className="size-4" aria-hidden />
            <span className="sr-only">Cuadros</span>
          </button>
          <button
            type="button"
            onClick={() => changeView('list')}
            aria-pressed={view === 'list'}
            title="Ver en listado"
            className={cn(
              'inline-flex size-9 cursor-pointer items-center justify-center rounded-lg transition-colors',
              view === 'list'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <List className="size-4" aria-hidden />
            <span className="sr-only">Listado</span>
          </button>
        </div>
      </div>

      {query.isError && (
        <Card className="mb-4">
          <CardBody className="text-sm text-danger">
            {(query.error as Error).message}
          </CardBody>
        </Card>
      )}

      {query.isLoading ? (
        <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3')}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-56 animate-pulse rounded-2xl border border-border bg-muted"
            />
          ))}
        </div>
      ) : vehicles.length === 0 ? (
        <Card>
          <EmptyState
            title="Sin vehículos"
            description="Importa el Excel histórico con npm run seed:excel o añade la primera unidad."
            icon={Truck}
          />
        </Card>
      ) : view === 'grid' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {vehicles.map((v) => (
            <VehicleCard key={v.id} v={v} />
          ))}
        </div>
      ) : (
        <Card className="divide-y divide-border overflow-hidden">
          {vehicles.map((v) => (
            <VehicleRow key={v.id} v={v} />
          ))}
        </Card>
      )}
    </>
  )
}
